import React, {
  memo,
  useRef,
  useEffect,
  useState,
  MutableRefObject,
} from 'react';
import equal from 'fast-deep-equal/es6/react';
import clone from 'lodash/clone';
import lottie, {
  AnimationConfig,
  AnimationDirection,
  AnimationEventCallback,
  AnimationSegment,
} from 'lottie-web';

export type LottieProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> &
  Partial<
    Pick<AnimationConfig, 'loop' | 'renderer' | 'rendererSettings'> & {
      play?: boolean;
      goTo?: number;
      speed?: number;
      direction?: AnimationDirection;
      segments?: AnimationSegment | AnimationSegment[];

      onComplete?: AnimationEventCallback;
      onLoopComplete?: AnimationEventCallback;
      onEnterFrame?: AnimationEventCallback;
      onSegmentStart?: AnimationEventCallback;
      path?: string;
      animationData?: any;
      onLoad: () => any;
    }
  >;

const emptyFunc = () => {};

export const Lottie = memo(
  ({
    animationData,
    path,

    play,
    speed = 1,
    direction = 1,
    segments: segmentsIn,
    goTo,

    renderer = 'svg',
    loop = true,
    rendererSettings: rendererSettingsIn = {},

    onLoad = emptyFunc,
    onComplete = emptyFunc,
    onLoopComplete = emptyFunc,
    onEnterFrame = emptyFunc,
    onSegmentStart = emptyFunc,

    ...props
  }: LottieProps) => {
    const animElementRef = useRef<HTMLDivElement | null>(null);
    const animRef = useRef() as MutableRefObject<any>;

    const [ready, setReady] = useState(false);

    const [segments, setSegments] = useState(segmentsIn);

    // Prevent re-init
    useEffect(() => {
      if (!equal(segments, segmentsIn)) setSegments(segmentsIn);
    }, [segmentsIn, segments]);

    const [rendererSettings, setRendererSettings] =
      useState(rendererSettingsIn);

    // Prevent re-init
    useEffect(() => {
      if (!equal(rendererSettings, rendererSettingsIn))
        setRendererSettings(rendererSettingsIn);
    }, [rendererSettingsIn, rendererSettings]);

    // In order to remove listeners before animRef gets destroyed:
    useEffect(
      () => () =>
        animRef.current &&
        animRef.current.removeEventListener('complete', onComplete),
      [onComplete, animRef],
    );
    useEffect(
      () => () =>
        animRef.current &&
        animRef.current.removeEventListener('loopComplete', onLoopComplete),
      [onLoopComplete, animRef],
    );
    useEffect(
      () => () =>
        animRef.current &&
        animRef.current.removeEventListener('enterFrame', onEnterFrame),
      [onEnterFrame, animRef],
    );
    useEffect(
      () => () =>
        animRef.current &&
        animRef.current.removeEventListener('segmentStart', onSegmentStart),
      [onSegmentStart, animRef],
    );

    useEffect(() => {
      function parseAnimationData() {
        if (!animationData || typeof animationData !== 'object')
          return animationData;

        // https://github.com/mifi/react-lottie-player/issues/11#issuecomment-879310039
        // https://github.com/chenqingspring/vue-lottie/issues/20
        if (typeof animationData.default === 'object') {
          return clone(animationData.default);
        }
        // cloneDeep to prevent memory leak. See #35
        return clone(animationData);
      }
      if (!animElementRef.current) {
        return;
      }

      animRef.current = lottie.loadAnimation({
        animationData: parseAnimationData(),
        path,
        container: animElementRef.current,
        renderer,
        loop: false,
        autoplay: false, // We want to explicitly control playback
        rendererSettings,
      });

      function onDomLoaded() {
        setReady(true);
        onLoad();
      }
      animRef.current.addEventListener('DOMLoaded', onDomLoaded);

      return () => {
        animRef.current.removeEventListener('DOMLoaded', onDomLoaded);
        setReady(false);
        animRef.current.destroy();
        animRef.current = undefined;
      };
    }, [
      loop,
      renderer,
      rendererSettings,
      animationData,
      path,
      animRef,
      onLoad,
    ]);

    useEffect(() => {
      animRef.current.addEventListener('complete', onComplete);
    }, [onComplete, animRef]);

    useEffect(() => {
      animRef.current.addEventListener('loopComplete', onLoopComplete);
    }, [onLoopComplete, animRef]);

    useEffect(() => {
      animRef.current.addEventListener('enterFrame', onEnterFrame);
    }, [onEnterFrame, animRef]);

    useEffect(() => {
      animRef.current.addEventListener('segmentStart', onSegmentStart);
    }, [onSegmentStart, animRef]);

    useEffect(() => {
      if (!ready) return;
      animRef.current.loop = loop;
    }, [ready, loop, animRef]);

    const wasPlayingSegmentsRef = useRef(false);

    useEffect(() => {
      if (!ready) return;

      // Without this code, when playback restarts, it will not play in reverse:
      // https://github.com/mifi/react-lottie-player/issues/19
      function playReverse(lastFrame: any) {
        animRef.current.goToAndPlay(lastFrame, true);
        animRef.current.setDirection(direction);
      }

      if (play === true) {
        const force = true;
        if (segments) {
          animRef.current.playSegments(segments, force);
          wasPlayingSegmentsRef.current = true;

          // This needs to be called after playSegments or it will not play backwards
          if (direction === -1) {
            // TODO What if more than one segment
            const lastFrame = segments[1];
            playReverse(lastFrame);
          }
        } else {
          // If we called playSegments last time, the segments are stored as a state in the lottie object
          // Need to reset segments or else it will still play the old segments also when calling play()
          // wasPlayingSegmentsRef: Only reset segments if playSegments last time, because resetSegments will also reset playback position
          // https://github.com/airbnb/lottie-web/blob/master/index.d.ts
          if (wasPlayingSegmentsRef.current)
            animRef.current.resetSegments(force);
          wasPlayingSegmentsRef.current = false;

          if (direction === -1) {
            const lastFrame = animRef.current.getDuration(true);
            playReverse(lastFrame);
          } else {
            animRef.current.play();
          }
        }
      } else if (play === false) {
        animRef.current.pause();
      }
    }, [play, segments, ready, animRef, direction]);

    useEffect(() => {
      if (!ready) return;
      if (Number.isNaN(speed)) return;
      animRef.current.setSpeed(speed);
    }, [speed, ready, animRef]);

    // This handles the case where only direction has changed (direction is not a dependency of the other effect that calls setDirection)
    useEffect(() => {
      if (!ready) return;
      animRef.current.setDirection(direction);
    }, [direction, ready, animRef]);

    useEffect(() => {
      if (!ready) return;
      if (goTo == null) return;
      const isFrame = true; // TODO
      if (play) animRef.current.goToAndPlay(goTo, isFrame);
      else animRef.current.goToAndStop(goTo, isFrame);
    }, [goTo, play, ready, animRef]);

    return (
      <div
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        ref={animElementRef}
      />
    );
  },
);
